import api, { ApiResponse } from './api';
import type { CompanyVerificationStatus } from '@safetyhub/shared';

export type { CompanyVerificationStatus };

export interface Company {
  id: string;
  name: string;
  registrationNumber?: string;
  ceoName?: string;
  openingDate?: string; // YYYYMMDD
  industry?: string; // 업종 (Organization에서 이관)
  contact?: string;
  email?: string;
  address?: string;
  zonecode?: string;
  addressDetail?: string;
  verificationStatus?: CompanyVerificationStatus;
  /**
   * 정본 클레임 시각 (null = 미클레임). 전역 정본 자격은 FULLY가 아니라 이 축이다.
   * 조직 조회(GET /organizations/:id)의 `company`는 Company 행 전체를 내려주므로 여기 포함된다.
   *
   * ⚠️ **이 타입에 선언돼 있다고 «어느 응답에서나» 온다는 뜻이 아니다 — 서버가 라우트별로 다른
   * `select`를 싣는다.** 2026-10-21부터 현장 상세·수정(`GET`·`PATCH /organizations/:orgId/sites/:siteId`)의
   * `site.company`는 **6칸뿐**이고(`id`·`name`·`registrationNumber`·`ceoName`·`openingDate`·
   * `verificationStatus` — 서버 `SITE_DETAIL_COMPANY_SELECT`가 정본) `claimedAt`은 **거기 없다**.
   * 좁히기 전에는 그 응답이 Company 전 스칼라를 실어 타사 업체의 심사 내부 상태까지 나갔다.
   * ⇒ 새 자리에서 이 타입의 칸을 읽기 전에 **그 라우트의 select를 확인할 것**(타입은 그대로이고
   * 런타임 값만 `undefined`가 되므로 tsc가 못 잡는다).
   */
  claimedAt?: string | null;
  createdByOrgId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCompanyDto {
  name: string;
  registrationNumber?: string;
  ceoName?: string;
  openingDate?: string;
  industry?: string;
  contact?: string;
  email?: string;
  address?: string;
  zonecode?: string;
  addressDetail?: string;
}

export interface UpdateCompanyDto extends Partial<CreateCompanyDto> {
  reason?: string;
}

export interface CompanyAuditLog {
  id: string;
  companyId: string;
  userId: string;
  action: 'CREATE' | 'UPDATE';
  before?: Record<string, unknown>;
  after?: Record<string, unknown>;
  reason?: string;
  createdAt: string;
}

// NTS 검증/조회용 lookup 응답
export interface CompanyLookupHit {
  id: string;
  name: string;
  registrationNumber: string | null;
  ceoName: string | null;
  openingDate: string | null;
  address: string | null;
  zonecode: string | null;
  addressDetail: string | null;
  verificationStatus: CompanyVerificationStatus;
  ntsVerifiedAt: string | null;
  ntsBusinessStatus: string | null;
  /** 클레임 정본 시각 (2026-08-14 P2: 전역 정본 자격은 FULLY가 아니라 클레임 축) */
  claimedAt: string | null;
  /** claimedAt !== null — 클레임 정본 여부 (구 subscription 유무 판단에서 의미 전환) */
  isClaimed: boolean;
  isStale: boolean;
}

export type CompanyLookupResult =
  | { found: false }
  | { found: true; company: CompanyLookupHit };

// NTS 실시간 검증 요청/응답 (서버 POST /companies/verify)
export interface CompanyVerifyInput {
  registrationNumber: string;
  businessName: string;
  ceoName: string;
  openingDate: string; // YYYYMMDD 8자리
}

export interface CompanyVerifySuccess {
  ok: true;
  registrationNumber: string; // normalized
  canonicalName: string; // NTS 정식 상호
  bSttCd: '01';
}

// 번호만 검증 (/status 단독) — 3단계 STATUS_VERIFIED 경로
export interface CompanyVerifyStatusSuccess {
  ok: true;
  registrationNumber: string;
  bSttCd: '01';
}

// Site 생성/수정에 포함되는 수검업체 정보 (서버 CompanyInfoDto와 일치)
export interface CompanyInfoInput {
  verified: boolean;
  registrationNumber?: string;
  businessName?: string;
  ceoName?: string;
  openingDate?: string;
  companyAddress?: string;
  companyZonecode?: string;
  companyAddressDetail?: string;
}

// 정본 클레임 (대표 문자 인증 2단) — POST /companies/:id/claim/request → .../confirm
export interface ClaimRequestInput {
  ceoName: string;
  /** 하이픈/공백 포함 가능 (서버 검증 /^[\d\s-]{9,20}$/) */
  phone: string;
}

export interface ClaimRequestResult {
  /** 인증 코드 만료 시각 (ISO, UTC) */
  expiresAt: string;
}

export interface ClaimConfirmInput extends ClaimRequestInput {
  /** SMS 인증 코드 6자리 */
  code: string;
}

export interface ClaimConfirmResult {
  claimedAt: string;
  /** 흡수된 같은 사업자번호의 빈 껍데기 Company 수 */
  mergedCount: number;
  mergedCompanyIds: string[];
  /** 정본으로 재연결된 현장 수 */
  reattachedSiteCount: number;
  /** 데이터가 있어 흡수하지 않고 남겨둔 Company */
  skippedCompanyIds: string[];
}

/** GET /companies 응답 — 서버는 배열이 아니라 커서 페이지를 내려준다. */
export interface CompanySearchResult {
  items: Company[];
  nextCursor: string | null;
}

export const companiesApi = {
  // ⚠️ companyId를 쿼리로 보내지 말 것 — SearchCompanyDto가 forbidNonWhitelisted로 400을 낸다
  // (2026-08-15 테넌트 통합 실측에서 적발. 이 엔드포인트는 인증만으로 조회하는 검색이다)
  search: async (query: {
    q?: string;
    registrationNumber?: string;
    limit?: number;
  }): Promise<CompanySearchResult> => {
    const params = new URLSearchParams();
    if (query.q) params.set('q', query.q);
    if (query.registrationNumber) params.set('registrationNumber', query.registrationNumber);
    if (query.limit !== undefined) params.set('limit', String(query.limit));
    const qs = params.toString();
    const response = await api.get<ApiResponse<CompanySearchResult>>(
      `/companies${qs ? `?${qs}` : ''}`,
    );
    return response.data.data ?? { items: [], nextCursor: null };
  },

  // companyId(두 번째 인자)는 호출 조직 — 서버가 자신/등록 행/현장 link 연결 조직만 열람 허용 (W-6)
  findById: async (id: string, companyId: string): Promise<Company> => {
    const response = await api.get<ApiResponse<Company>>(
      `/companies/${id}?companyId=${companyId}`,
    );
    return response.data.data!;
  },

  create: async (dto: CreateCompanyDto, companyId: string): Promise<Company> => {
    const response = await api.post<ApiResponse<Company>>(
      `/companies?companyId=${companyId}`,
      dto,
    );
    return response.data.data!;
  },

  update: async (id: string, dto: UpdateCompanyDto, companyId: string): Promise<Company> => {
    const response = await api.patch<ApiResponse<Company>>(
      `/companies/${id}?companyId=${companyId}`,
      dto,
    );
    return response.data.data!;
  },

  // companyId(두 번째 인자)는 update와 같은 호출 조직 — 서버가 연결 현장 보유 조직만 열람 허용
  getAuditLogs: async (id: string, companyId: string): Promise<CompanyAuditLog[]> => {
    const response = await api.get<ApiResponse<CompanyAuditLog[]>>(
      `/companies/${id}/audit-logs?companyId=${companyId}`,
    );
    return response.data.data ?? [];
  },

  lookup: async (registrationNumber: string): Promise<CompanyLookupResult> => {
    const response = await api.get<ApiResponse<CompanyLookupResult>>(
      `/companies/lookup?registrationNumber=${encodeURIComponent(registrationNumber)}`,
    );
    return response.data.data ?? { found: false };
  },

  verify: async (input: CompanyVerifyInput): Promise<CompanyVerifySuccess> => {
    const response = await api.post<ApiResponse<CompanyVerifySuccess>>(
      '/companies/verify',
      input,
    );
    return response.data.data!;
  },

  // 클레임 1단 — 대표 휴대폰으로 인증 코드 발송.
  // companyId(두 번째 인자)는 호출 조직 = 자기 회사 id. 회사=조직 구조라 서버가 id와 동일할 때만 허용한다.
  requestClaim: async (
    id: string,
    companyId: string,
    input: ClaimRequestInput,
  ): Promise<ClaimRequestResult> => {
    const response = await api.post<ApiResponse<ClaimRequestResult>>(
      `/companies/${id}/claim/request?companyId=${companyId}`,
      input,
    );
    return response.data.data!;
  },

  // 클레임 2단 — 코드 검증 + claimedAt 확정 + 같은 사업자번호 빈 껍데기 흡수
  confirmClaim: async (
    id: string,
    companyId: string,
    input: ClaimConfirmInput,
  ): Promise<ClaimConfirmResult> => {
    const response = await api.post<ApiResponse<ClaimConfirmResult>>(
      `/companies/${id}/claim/confirm?companyId=${companyId}`,
      input,
    );
    return response.data.data!;
  },

  verifyStatus: async (registrationNumber: string): Promise<CompanyVerifyStatusSuccess> => {
    const response = await api.post<ApiResponse<CompanyVerifyStatusSuccess>>(
      '/companies/verify-status',
      { registrationNumber },
    );
    return response.data.data!;
  },
};
